import { useEffect, useRef, useState } from "react";

/**
 * Hook to detect user inactivity.
 * @param timeoutMs The number of milliseconds before inactivity is triggered.
 * @returns {boolean} isActive: true if the user has been inactive for timeoutMs.
 */
export function useInactivity(timeoutMs: number = 300000) {
    const [isActive, setIsActive] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const resetTimer = () => {
            setIsActive(false);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                setIsActive(true);
            }, timeoutMs);
        };

        // Initial timer
        resetTimer();

        const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];
        const handleActivity = () => resetTimer();

        events.forEach((event) => window.addEventListener(event, handleActivity));

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            events.forEach((event) => window.removeEventListener(event, handleActivity));
        };
    }, [timeoutMs]);

    return isActive;
}
